"use client"

import { useEffect, useState } from "react"
import ResponsibleGambling from "./ResponsibleGambling"
import Statistics from "./Statistics"
import { loadStripe } from "@stripe/stripe-js"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import TransactionHistory from "./TransactionHistory"
import { getFunctions, httpsCallable } from "firebase/functions"
import RequestWithdrawal from "./RequestWithdrawal"
import { Elements } from "@stripe/react-stripe-js"
import Header from "./header"
import AddFunds2 from "./AddFunds2"
import Support from "./Support"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../firebase"
import { User, CreditCard, FileText, Shield, BarChart2, LogOut, Upload, Wallet, MessageCircle } from "lucide-react"


const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(stripePublishableKey);


function Settings() {
  const [activeTab, setActiveTab] = useState("accountInfo")
  const navigate = useNavigate()
  const { currentUser, userProfile, logout } = useAuth()
  const [amount, setAmount] = useState(0)
  const [withdrawalMethod, setWithdrawalMethod] = useState("bank")
  const [paypalEmail, setPaypalEmail] = useState("")
  const functions = getFunctions()
  const createPaypalWithdrawal = httpsCallable(functions, "createPaypalWithdrawal")
  const [currentView, setCurrentView] = useState("settings")
  const [showNotifications, setShowNotifications] = useState(false)
  const [profilePicture, setProfilePicture] = useState(userProfile?.profile_picture || "")
  const [profilePictureFile, setProfilePictureFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const [searchParams] = useSearchParams()
  const defaultTab = searchParams.get("tab")

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab)
    }
  }, [defaultTab])

  async function checkTodaysWithdrawal() {
    return 0
  }

  const requestWithdrawalManual = async () => {
    if (withdrawalMethod === "paypal" && !paypalEmail) {
      alert("Please enter a valid PayPal email address.")
      return
    }

    try {
      if (withdrawalMethod === "paypal") {
        console.log("PayPal withdrawal response:")
      } else if (withdrawalMethod === "bank") {
        console.log("Processing bank withdrawal")
      } else if (withdrawalMethod === "card") {
        console.log("Processing card withdrawal")
      }
    } catch (error) {
      console.error("Error processing withdrawal:", error)
      alert("Error processing withdrawal. Please try again.")
    }
  }

  const handleWithdrawal = () => {
    if (amount <= 0) {
      alert("Enter a valid amount")
      return
    }

    if (amount < 10) {
      alert("Minimum Withdrawal Amount is $10")
      return
    }

    if (amount >= 1000) {
      alert("Your withdrawal request is sent and is now in review.")
      requestWithdrawalManual()
      return
    }

    console.log("Passed all checks Withdrawal Amount: ", amount)
    requestWithdrawalManual()
  }

  // Profile Picture Upload Handler
  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Validate file type and size
      const validTypes = ["image/jpeg", "image/png", "image/gif"]
      const maxSize = 5 * 1024 * 1024 // 5MB

      if (!validTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPEG, PNG, or GIF).")
        return
      }

      if (file.size > maxSize) {
        alert("File size should be less than 5MB.")
        return
      }

      setProfilePictureFile(file)
      setProfilePicture(URL.createObjectURL(file))
    }
  }

  // Upload Profile Picture to Firebase Storage
  const uploadProfilePicture = async () => {
    if (!profilePictureFile) {
      alert("Please select an image first.")
      return
    }

    setIsUploading(true)

    try {
      // Get Firebase Storage reference
      const storage = getStorage()
      const storageRef = ref(storage, `profile_pictures/${currentUser.uid}/${Date.now()}_${profilePictureFile.name}`)

      // Upload file
      const snapshot = await uploadBytes(storageRef, profilePictureFile)

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref)

      // Update user profile in Firestore
      const userRef = doc(db, "users", currentUser.uid)
      await updateDoc(userRef, {
        profile_picture: downloadURL,
      })

      // Update local state
      setProfilePicture(downloadURL)
      setProfilePictureFile(null)

      alert("Profile picture uploaded successfully!")
    } catch (error) {
      console.error("Error uploading profile picture:", error)
      alert("Failed to upload profile picture. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "accountInfo":
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <User className="w-6 h-6 text-emerald-400 mr-2" />
              Account Information
            </h2>

            {/* Account Info Form */}
            <div className="mb-8 flex flex-col items-center">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-4 border-4 border-gray-800 bg-gray-800">
                <img
                  src={profilePicture || "https://pics.craiyon.com/2023-07-15/3cec1ec2b257464db9bf7bec61cc78d7.webp"}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  onChange={handleProfilePictureChange}
                  className="hidden"
                  id="profilePictureInput"
                />
                <label
                  htmlFor="profilePictureInput"
                  className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer flex items-center"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Change Picture
                </label>
                {profilePictureFile && (
                  <button
                    onClick={uploadProfilePicture}
                    disabled={isUploading}
                    className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-6">
              <form>
                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input
                    disabled
                    type="text"
                    placeholder="Name"
                    defaultValue={userProfile?.full_name}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input
                    disabled
                    type="email"
                    placeholder="Email"
                    defaultValue={userProfile?.email}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Phone */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                  <input
                    disabled
                    type="tel"
                    placeholder="Phone"
                    defaultValue={userProfile?.phone}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Address */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                  <textarea
                    disabled
                    rows="2"
                    placeholder="Address"
                    defaultValue={userProfile?.address}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Submit Button */}
                {/* <button
                  type="submit"
                  className="w-full bg-emerald-500 text-white py-3 rounded-lg hover:bg-emerald-600 transition-colors font-medium"
                >
                  Save Changes
                </button> */}
              </form>
            </div>
          </div>
        )
      case "requestWithdrawal":
        return (
          <Elements stripe={stripePromise}>
            <RequestWithdrawal userProfile={userProfile} />
          </Elements>
        )
      case "transactionHistory":
        return (
          <div>
            <TransactionHistory />
          </div>
        )
      case "deposit":
        return (
          <div>
            <AddFunds2 />
          </div>
        )
      case "support":
        return (
          <div>
            <Support />
          </div>
        )
      case "taxInformation":
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <FileText className="w-6 h-6 text-emerald-400 mr-2" />
              Tax Information
            </h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-6">
              <p className="text-gray-300">Here you can view and update your tax information.</p>
              <p className="text-gray-400 mt-4">This feature is coming soon.</p>
            </div>
          </div>
        )
      case "responsibleGambling":
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Shield className="w-6 h-6 text-emerald-400 mr-2" />
              Responsible Gambling
            </h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-6">
              <ResponsibleGambling />
            </div>
          </div>
        )
      case "statistics":
        return (
          <div>
            <Statistics />
          </div>
        )
      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-300">Select a tab to view content.</p>
          </div>
        )
    }
  }

  return (
    <div className="bg-gradient-to-b from-gray-900 to-black">
      <Header onToggle={setCurrentView} currentView={currentView} onShowNotifications={setShowNotifications} />
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex flex-col lg:flex-row p-4 border-gray-800 bg-gray-900/90 backdrop-blur-md shadow-lg">
        {/* Sidebar */}
        <div className="w-full lg:w-1/4 bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-6 mb-4 lg:mb-0 lg:mr-4">
          <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>

          {isMobile ? (
            <div className="overflow-x-auto pb-4">
              <div className="flex space-x-3">
                <button
                  onClick={() => setActiveTab("accountInfo")}
                  className={`p-3 rounded-lg flex flex-col items-center min-w-[80px] ${
                    activeTab === "accountInfo"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  } transition-colors`}
                >
                  <User className="w-5 h-5 mb-1" />
                  <span className="text-xs">Account</span>
                </button>
                <button
                  onClick={() => setActiveTab("deposit")}
                  className={`p-3 rounded-lg flex flex-col items-center min-w-[80px] ${
                    activeTab === "deposit"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  } transition-colors`}
                >
                  <CreditCard className="w-5 h-5 mb-1" />
                  <span className="text-xs">Deposit</span>
                </button>
                <button
                  onClick={() => setActiveTab("requestWithdrawal")}
                  className={`p-3 rounded-lg flex flex-col items-center min-w-[80px] ${
                    activeTab === "requestWithdrawal"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  } transition-colors`}
                >
                  <Wallet className="w-5 h-5 mb-1" />
                  <span className="text-xs">Withdraw</span>
                </button>
                <button
                  onClick={() => setActiveTab("transactionHistory")}
                  className={`p-3 rounded-lg flex flex-col items-center min-w-[80px] ${
                    activeTab === "transactionHistory"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  } transition-colors`}
                >
                  <FileText className="w-5 h-5 mb-1" />
                  <span className="text-xs">History</span>
                </button>
                <button
                  onClick={() => setActiveTab("statistics")}
                  className={`p-3 rounded-lg flex flex-col items-center min-w-[80px] ${
                    activeTab === "statistics"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  } transition-colors`}
                >
                  <BarChart2 className="w-5 h-5 mb-1" />
                  <span className="text-xs">Stats</span>
                </button>
                <button
                  onClick={() => setActiveTab("support")}
                  className={`p-3 rounded-lg flex flex-col items-center min-w-[80px] ${
                    activeTab === "support"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  } transition-colors`}
                >
                  <MessageCircle className="w-5 h-5 mb-1" />
                  <span className="text-xs">Support</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => setActiveTab("accountInfo")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "accountInfo"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <User className="w-5 h-5 mr-3" />
                Account Info
              </button>
              <button
                onClick={() => setActiveTab("deposit")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "deposit" ? "bg-emerald-500 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <CreditCard className="w-5 h-5 mr-3" />
                Deposit
              </button>
              <button
                onClick={() => setActiveTab("requestWithdrawal")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "requestWithdrawal"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <Wallet className="w-5 h-5 mr-3" />
                Request Withdrawal
              </button>
              <button
                onClick={() => setActiveTab("transactionHistory")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "transactionHistory"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <FileText className="w-5 h-5 mr-3" />
                Transaction History
              </button>
              <button
                onClick={() => setActiveTab("taxInformation")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "taxInformation"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <FileText className="w-5 h-5 mr-3" />
                Tax Information
              </button>
              <button
                onClick={() => setActiveTab("responsibleGambling")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "responsibleGambling"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <Shield className="w-5 h-5 mr-3" />
                Responsible Gambling
              </button>
              <button
                onClick={() => setActiveTab("statistics")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "statistics"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <BarChart2 className="w-5 h-5 mr-3" />
                Statistics
              </button>
              <button
                onClick={() => setActiveTab("support")}
                className={`p-3 rounded-lg flex items-center ${
                  activeTab === "support"
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                } transition-colors`}
              >
                <MessageCircle className="w-5 h-5 mr-3" />
                Support
              </button>
              <div className="border-t border-gray-700 my-3"></div>
              <button
                onClick={() => logout()}
                className="p-3 rounded-lg flex items-center bg-red-500/80 text-white hover:bg-red-600 transition-colors"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="w-full lg:w-3/4 bg-gray-900/30 backdrop-blur-sm rounded-xl border border-gray-800 p-6 flex-grow">
          {renderTabContent()}
        </div>
      </div>
    </div>
  )
}

export default Settings

